import { useState } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export type PartnerCodeFilterField = "status" | "type" | "partner" | "code" | "uses" | "creditsValue";
export type PartnerCodeFilterOperator = "contains" | "equals" | ">=" | "<=" | ">" | "<";

export interface PartnerCodeFilterTag {
  id: string;
  field: PartnerCodeFilterField;
  value: string;
  label: string;
  operator?: PartnerCodeFilterOperator;
}

interface PartnerCodesColumnFiltersProps {
  filterTags: PartnerCodeFilterTag[];
  addFilter: (field: PartnerCodeFilterField, value: string, operator?: PartnerCodeFilterOperator) => void;
  removeFilter: (id: string) => void;
  clearAllFilters: () => void;
}

const FIELD_OPTIONS: {
  value: PartnerCodeFilterField;
  label: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  operators?: PartnerCodeFilterOperator[];
}[] = [
  {
    value: "status",
    label: "Status",
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
  },
  {
    value: "type",
    label: "Type",
    options: [
      { value: "credits", label: "Credits" },
      { value: "percentage", label: "Percentage" },
    ],
  },
  {
    value: "partner",
    label: "Partner Name",
    placeholder: "Search partner...",
    operators: ["contains", "equals"],
  },
  {
    value: "code",
    label: "Code",
    placeholder: "Search code...",
    operators: ["contains", "equals"],
  },
  {
    value: "uses",
    label: "Uses",
    placeholder: "Enter count...",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
  {
    value: "creditsValue",
    label: "Credits Value",
    placeholder: "Enter value...",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
];

const FIELD_COLORS: Record<PartnerCodeFilterField, string> = {
  status: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  type: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  partner: "bg-purple-500/10 text-purple-600 border-purple-500/30",
  code: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  uses: "bg-cyan-500/10 text-cyan-600 border-cyan-500/30",
  creditsValue: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
};

const OPERATOR_LABELS: Record<PartnerCodeFilterOperator, string> = {
  contains: "contains",
  equals: "=",
  ">=": "≥",
  "<=": "≤",
  ">": ">",
  "<": "<",
};

export const PartnerCodesColumnFilters = ({
  filterTags,
  addFilter,
  removeFilter,
  clearAllFilters,
}: PartnerCodesColumnFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<PartnerCodeFilterField>("status");
  const [selectedOperator, setSelectedOperator] = useState<PartnerCodeFilterOperator>("contains");
  const [inputValue, setInputValue] = useState("");

  const selectedFieldConfig = FIELD_OPTIONS.find((f) => f.value === selectedField);
  const hasOptions = !!selectedFieldConfig?.options;
  const hasOperators = !!selectedFieldConfig?.operators && selectedFieldConfig.operators.length > 0;

  const handleAddFilter = () => {
    if (inputValue.trim()) {
      const operator = hasOperators ? selectedOperator : undefined;
      addFilter(selectedField, inputValue.trim(), operator);
      setInputValue("");
      setIsOpen(false);
    }
  };

  const handleSelectOption = (value: string) => {
    addFilter(selectedField, value);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddFilter();
    }
  };

  const handleFieldChange = (field: PartnerCodeFilterField) => {
    setSelectedField(field);
    setInputValue("");
    const fieldConfig = FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldConfig?.operators?.length) {
      setSelectedOperator(fieldConfig.operators[0]);
    }
  };

  const formatTagDisplay = (tag: PartnerCodeFilterTag) => {
    if (tag.operator) {
      return `${OPERATOR_LABELS[tag.operator]} ${tag.value}`;
    }
    return tag.label;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filter
            {filterTags.length > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {filterTags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Field</span>
              <div className="flex flex-wrap gap-1">
                {FIELD_OPTIONS.map((field) => (
                  <Button
                    key={field.value}
                    variant={selectedField === field.value ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleFieldChange(field.value)}
                  >
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {hasOperators && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operator</span>
                <div className="flex flex-wrap gap-1">
                  {selectedFieldConfig?.operators?.map((op) => (
                    <Button
                      key={op}
                      variant={selectedOperator === op ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs min-w-[40px]"
                      onClick={() => setSelectedOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Value</span>
              {hasOptions ? (
                <div className="flex flex-wrap gap-1">
                  {selectedFieldConfig?.options?.map((option) => (
                    <Button
                      key={option.value}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleSelectOption(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder={selectedFieldConfig?.placeholder || "Enter value..."}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                    type={["uses", "creditsValue"].includes(selectedField) ? "number" : "text"}
                  />
                  <Button size="sm" className="h-8" onClick={handleAddFilter}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {filterTags.map((tag) => (
        <span
          key={tag.id}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium",
            FIELD_COLORS[tag.field]
          )}
        >
          <span className="opacity-70">{tag.field}:</span>
          {formatTagDisplay(tag)}
          <button
            onClick={() => removeFilter(tag.id)}
            className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      {filterTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={clearAllFilters}
        >
          Clear all
        </Button>
      )}
    </div>
  );
};
